// src/components/FirebaseLoginButton.tsx
import React, { useState } from 'react';
import { firebaseAuthService, FirebaseUser } from '../services/firebaseAuth.service';

interface FirebaseLoginButtonProps {
  onAuthSuccess: (user: FirebaseUser) => void;
  onAuthError: (error: Error) => void;
  onPasswordReset?: (email: string) => void;
  className?: string;
}

export const FirebaseLoginButton: React.FC<FirebaseLoginButtonProps> = ({ 
  onAuthSuccess, 
  onAuthError,
  onPasswordReset,
  className = "w-full px-4 py-3 text-primary-foreground bg-primary rounded-lg hover:brightness-105 disabled:opacity-50 button-sanctuary"
}) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [name, setName] = useState('');
  const [localMessage, setLocalMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);

  // Defensive type checks and coercion for email and password
  const safeEmail = (() => {
    if (typeof email !== 'string') {
      console.warn(`[FirebaseLoginButton] Warning: email state is not a string. Coercing to string. Value:`, email);
      return String(email ?? '');
    }
    return email;
  })();

  const safePassword = (() => {
    if (typeof password !== 'string') {
      console.warn(`[FirebaseLoginButton] Warning: password state is not a string. Coercing to string. Value:`, password);
      return String(password ?? '');
    }
    return password;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!safeEmail || !safePassword) {
      setLocalMessage({ type: 'error', text: 'Please enter both email and password' });
      return;
    }

    setLoading(true);
    setLocalMessage(null);
    
    try {
      console.log(`[FirebaseLoginButton] Starting ${isSignUp ? 'sign up' : 'sign in'}...`);
      const user = isSignUp 
        ? await firebaseAuthService.signUpWithEmail(safeEmail, safePassword)
        : await firebaseAuthService.signInWithEmail(safeEmail, safePassword);
      console.log('[FirebaseLoginButton] Authentication successful:', user);
      // If signing up and name provided, update displayName in Firebase
      if (isSignUp && name.trim()) {
        try {
          await firebaseAuthService.updateDisplayName(name.trim());
        } catch (e) {
          console.warn('[FirebaseLoginButton] Failed to update displayName, continuing:', e);
        }
      }
      
      // Call onAuthSuccess
      onAuthSuccess(user);
      console.log('[FirebaseLoginButton] onAuthSuccess callback completed');
      
    } catch (error: any) {
      console.error('[FirebaseLoginButton] Authentication error:', error);
      const errorMessage = error?.message || error?.toString() || 'Authentication failed';
      const errorObj = new Error(errorMessage);
      onAuthError(errorObj);
      setLocalMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordResetClick = async () => {
    if (passwordResetLoading || loading) {
      // Prevent double submits or spam clicks
      return;
    }

    if (!safeEmail) {
      setLocalMessage({ type: 'error', text: 'Please enter your email above to reset your password.' });
      return;
    }
    
    setPasswordResetLoading(true);
    setLocalMessage(null);
    
    try {
      console.log('[FirebaseLoginButton] Initiating password reset for:', safeEmail);
      
      if (onPasswordReset) {
        // If parent component handles it
        onPasswordReset(safeEmail);
        setLocalMessage({ type: 'success', text: 'Password reset requested. Please check your email.' });
      } else {
        // Direct call to service
        await firebaseAuthService.sendPasswordResetEmail(safeEmail);
        setLocalMessage({ type: 'success', text: 'Password reset email sent! Please check your inbox and spam folder.' });
      }
    } catch (error: any) {
      console.error('[FirebaseLoginButton] Password reset error:', error);
      const errorMessage = error?.message || error?.toString() || 'Failed to send password reset email';
      setLocalMessage({ type: 'error', text: errorMessage });
    } finally {
      setPasswordResetLoading(false);
    }
  };

  try {
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {isSignUp && (
          <div>
            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground placeholder:text-muted-foreground"
              required
              disabled={loading}
            />
          </div>
        )}
        <div>
          <input
            type="email"
            inputMode="email"
            placeholder="Email"
            value={safeEmail}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground placeholder:text-muted-foreground"
            required
            disabled={loading}
          />
        </div>
        <div>
          <input
            type="password"
            placeholder="Password"
            value={typeof safePassword === 'string' ? safePassword : ''}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground placeholder:text-muted-foreground"
            required
            disabled={loading}
          />
          <div className="text-right mt-2">
            <button
              type="button"
              className="text-sm text-primary hover:underline disabled:opacity-50"
              disabled={loading || passwordResetLoading}
              onClick={handlePasswordResetClick}
            >
              {passwordResetLoading ? 'Sending password reset...' : 'Forgot password?'}
            </button>
          </div>
        </div>
        
        {localMessage && (
          <div className={`p-3 rounded-lg text-sm ${
            localMessage.type === 'success' 
              ? 'bg-accent/10 text-accent-foreground border border-accent/20' 
              : 'bg-destructive/10 text-destructive-foreground border border-destructive/20'
          }`}>
            {typeof localMessage.text === 'string' && localMessage.text.trim() !== '' ? localMessage.text : (localMessage.type === 'success' ? 'Success.' : 'An error occurred.')}
          </div>
        )}
        
        <button
          type="submit"
          disabled={loading}
          className={className}
        >
          {loading ? 'Loading...' : (isSignUp ? 'Create Account' : 'Sign in with Email')}
        </button>
        <div className="text-center">
          <button
            type="button"
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-primary hover:brightness-105 text-sm"
            disabled={loading}
          >
            {isSignUp ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
          </button>
        </div>
      </form>
    );
  } catch (renderError) {
    console.error('[FirebaseLoginButton] Rendering error:', renderError);
    return (
      <div className="p-4 bg-destructive/10 text-destructive-foreground rounded-lg text-center">
        Something went wrong while rendering the login form. Please try refreshing the page.
      </div>
    );
  }
};